import moment from 'moment'
import 'moment/locale/zh-cn'
import 'moment/locale/pt'
import 'moment/locale/es'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/fr'
import 'moment/locale/nl'
import 'moment/locale/ja'
import { find, floor, orderBy, get, omit } from 'lodash'

import { palette, setOpacity } from '../styles/variables'
import { isValidSD } from '../state/actions/sdManager'

import { translate, getLanguageCode } from './translation'
import { formatNumber } from './numbers'
import { isGlobal } from './locations'
import { isPaidUser } from './freeVersionBlock'
import {
  AIWRITER_BASE_URL,
  MAX_KEYWORD_CHARACTER_LENGTH,
  skipPromptPaths,
} from './consts.mjs'
import { MIXPANEL_PROPS } from './mixpanel'
import {
  isDomain,
  isDomainWithoutHttp,
  isBaseUrl,
  isValidURL,
} from './url-validator'

moment.locale('en')

export const chooseSdColor = (value) => {
  if (value >= '70') {
    return [palette.red, setOpacity(palette.red, 0.2)]
  }
  if (value > '35') {
    return [palette.yellow, setOpacity(palette.yellow, 0.2)]
  }
  return [palette.green, setOpacity(palette.green, 0.2)]
}

export const chooseSpamColor = (value) => {
  if (value > 60) {
    return [palette.red, setOpacity(palette.red, 0.2)]
  }
  if (value > 30) {
    return [palette.yellow, setOpacity(palette.yellow, 0.2)]
  }
  return [palette.green, setOpacity(palette.green, 0.2)]
}

export const getDateFormat = (type = 'YYYY-MM-DD') => {
  const languageCode = getLanguageCode()
  const formats = {
    'YYYY-MM-DD': {
      en: 'MM/DD/YYYY',
      pt: 'DD/MM/YYYY',
      de: 'DD-MM-YYYY',
      es: 'DD-MM-YYYY',
      fr: 'DD/MM/YYYY',
      nl: 'DD-MM-YYYY',
      it: 'DD/MM/YYYY',
      ja: 'YYYY年MM月DD日',
      zh: 'YYYY年M月D日',
      default: 'YYYY-MM-DD',
    },
    'YY-MM': {
      en: 'YY/MM',
      pt: 'MM/YY',
      de: 'MM/YY',
      es: 'MM/YY',
      fr: 'MM/YY',
      nl: 'MM/YY',
      it: 'MM/YY',
      ja: 'YY年MM月',
      zh: 'YY年M月',
      default: 'YY/MM',
    },
    'MM-DD': {
      en: 'MM/DD',
      pt: 'DD/MM',
      de: 'DD-MM',
      es: 'DD-MM',
      fr: 'DD/MM',
      nl: 'DD-MM',
      it: 'DD/MM',
      ja: 'MM月DD日',
      zh: 'M月D日',
      default: 'MM-DD',
    },
  }
  return formats[type][languageCode]
}

export const formatDate = (date) => {
  if (date === '0000-00-00') return date
  const format = getDateFormat()
  if (typeof date === 'number') {
    return moment(date).format(format)
  }
  return moment(date, 'YYYY-MM-DD').format(format)
}

export const momentLangCode = () => {
  const MAPPING = {
    zh: 'zh_cn',
  }
  const lang = getLanguageCode()
  return MAPPING[lang] || lang
}

export const localizedMoment = (date) => {
  return moment(date).locale(momentLangCode())
}

export const formatTooltipDate = (date) => {
  const lang = getLanguageCode()
  moment.locale(lang)
  const dateObj = moment(Number(date) ? parseInt(date, 10) * 1000 : date)
  return dateObj.format('MMM DD')
}

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

export const compareStringByCharacters = (
  a,
  b,
  order,
  handleNumeric = false,
) => {
  const aNumber = handleNumeric && !isNaN(Number(a))
  const bNumber = handleNumeric && !isNaN(Number(b))

  const aValue = aNumber ? Number(a) : a.toUpperCase()
  const bValue = bNumber ? Number(b) : b.toUpperCase()

  if (aNumber === bNumber) {
    if (typeof aValue === 'string') {
      return order ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
    }
    if (aValue < bValue) {
      return order ? -1 : 1
    }
    if (aValue > bValue) {
      return order ? 1 : -1
    }
    return 1
  }
  if (aNumber && !bNumber) {
    return order ? 1 : -1
  }
  return order ? -1 : 1
}

export const compareString = (a, b, key, asc, handleNumeric) => {
  const re = /^([^A-zÀ-ÿ]).*$/
  const resultA = re.exec(a[key])
  const resultB = re.exec(b[key])
  if (resultA === null && resultB === null) {
    return compareStringByCharacters(a[key], b[key], asc, handleNumeric)
  }
  if (resultA !== null && resultB === null) {
    return asc ? 1 : -1
  }
  if (resultA === null && resultB !== null) {
    return asc ? -1 : 1
  }
  return compareStringByCharacters(a[key], b[key], asc, handleNumeric)
}

export const compareVolume = (a, b, key, asc) => {
  if (!a[key] && !b[key]) {
    return 1
  }
  if (!a[key]) {
    return asc ? 1 : -1
  }
  if (!b[key]) {
    return asc ? -1 : 1
  }
  if (parseInt(a[key], 10) < parseInt(b[key], 10)) {
    return asc ? 1 : -1
  }
  if (parseInt(a[key], 10) > parseInt(b[key], 10)) {
    return asc ? -1 : 1
  }
  return 0
}

export const compareCompetition = (a, b, asc) => {
  if (!a.competition && !b.competition) {
    return 1
  }
  if (!a.competition) {
    return asc ? 1 : -1
  }
  if (!b.competition) {
    return asc ? -1 : 1
  }
  if (a.competition < b.competition) {
    return asc ? 1 : -1
  }
  if (a.competition > b.competition) {
    return asc ? -1 : 1
  }
  return compareCpc(a, b, asc)
}

export const compareCpc = (a, b, key, asc) => {
  if (!a[key] && !b[key]) {
    return 1
  }
  if (!a[key]) {
    return asc ? 1 : -1
  }
  if (!b[key]) {
    return asc ? -1 : 1
  }
  if (parseFloat(a[key]) < parseFloat(b[key])) {
    return asc ? 1 : -1
  }
  if (parseFloat(a[key]) > parseFloat(b[key])) {
    return asc ? -1 : 1
  }
  return 0
}

export const compareDifficulty = (a, b, key, asc) => {
  if (!a[key] && !b[key]) {
    return 1
  }
  if (!a[key]) {
    return asc ? 1 : -1
  }
  if (!b[key]) {
    return asc ? -1 : 1
  }
  if (parseFloat(a[key]) < parseFloat(b[key])) {
    return asc ? 1 : -1
  }
  if (parseFloat(a[key]) > parseFloat(b[key])) {
    return asc ? -1 : 1
  }
  return 0
}

export const compareClicks = (a, b, key, asc) => {
  if (!a[key] && !b[key]) {
    return 1
  }
  if (!a[key]) {
    return asc ? 1 : -1
  }
  if (!b[key]) {
    return asc ? -1 : 1
  }
  if (parseFloat(a[key]) < parseFloat(b[key])) {
    return asc ? 1 : -1
  }
  if (parseFloat(a[key]) > parseFloat(b[key])) {
    return asc ? -1 : 1
  }
  return compareInt(a, b, asc)
}

export const compareInt = (a, b, asc) => {
  if (asc) {
    return parseInt(a.rank, 10) > parseInt(b.rank, 10) ? 1 : -1
  }
  return parseInt(a.rank, 10) > parseInt(b.rank, 10) ? -1 : 1
}

export const compareRank = (a, b, key, asc) => {
  if (a[key] === null && b[key] === null) {
    return 1
  }
  if (a[key] === null) {
    return asc ? -1 : 1
  }
  if (b[key] === null) {
    return asc ? 1 : -1
  }
  if (a[key] < b[key]) {
    return asc ? 1 : -1
  }
  if (a[key] > b[key]) {
    return asc ? -1 : 1
  }
  return compareInt(a, b, asc)
}

export const compareDomain = (a, b, asc) => {
  if (a.domain !== b.domain) {
    return compareString(a, b, 'domain', asc)
  }
  return compareInt(a, b, asc)
}

export const compareAuthority = (a, b, sortAttribute, asc) => {
  const domainA = parseInt(a[sortAttribute], 10)
  const domainB = parseInt(b[sortAttribute], 10)
  if (domainA > domainB) {
    return asc ? 1 : -1
  }
  if (domainB > domainA) {
    return asc ? -1 : 1
  }
  return compareInt(a, b, asc)
}

export const compareTopKeyowrd = (a, b, asc) => {
  if (a !== b) {
    return compareString(a, b, 'topKeyword', asc)
  }
  return compareInt(a, b, asc)
}

export const compareVolumeOrPosition = (a, b, asc) => {
  if (a !== b) {
    if (a.compareKey < b.compareKey) {
      return asc ? 1 : -1
    }
    if (a.compareKey > b.compareKey) {
      return asc ? -1 : 1
    }
  } else {
    return compareInt(a, b, asc)
  }
}

export const compareResults = (a, b, sortAttribute, asc, handleNumeric) => {
  switch (sortAttribute) {
    case 'keyword':
    case 'title':
    case 'url':
    case 'name':
    case 'page':
      return compareString(a, b, sortAttribute, asc, handleNumeric)
    case 'volume':
    case 'total_volume':
      return compareVolume(a, b, sortAttribute, asc)
    case 'competition':
      return compareCompetition(a, b, asc)
    case 'cpc':
    case 'avg_cpc':
    case 'cpcDollars':
      return compareCpc(a, b, sortAttribute, asc)
    case 'pd':
    case 'sd':
    case 'avg_pd':
    case 'avg_sd':
      return compareDifficulty(a, b, sortAttribute, asc)
    case 'clicks':
    case 'totalTraffic':
    case 'traffic':
    case 'backlinks':
    case 'shares':
    case 'external_pages':
    case 'keyword_count':
      return compareClicks(a, b, sortAttribute, asc)
    case 'desktop_change':
    case 'mobile_change':
    case 'desktop_new':
    case 'mobile_new':
      return compareRank(a, b, sortAttribute, asc)
    case 'rank':
      return compareInt(a, b, asc)
    case 'domain':
      return compareDomain(a, b, asc)
    case 'page_authority':
    case 'domain_authority':
      return compareAuthority(a, b, sortAttribute, asc)
    case 'keywords':
      a.keywords = a.list.length
      b.keywords = b.list.length
      return compareClicks(a, b, 'keywords', asc)
    case 'topKeyword':
      a.topKeyword = a.maxKw.keyword
      b.topKeyword = b.maxKw.keyword
      return compareTopKeyowrd(a, b, asc)
    case 'topKeywordVolume':
    case 'topKeywordPosition':
      if (sortAttribute === 'topKeywordVolume') {
        a.compareKey = a.maxKw.volume
        b.compareKey = b.maxKw.volume
      } else {
        a.compareKey = a.maxKw.position
        b.compareKey = b.maxKw.position
      }
      return compareVolumeOrPosition(a, b, asc)
    case 'position':
      return compareDifficulty(a, b, sortAttribute, asc)
    case 'commonKw':
    case 'seoKw':
    case 'paidKw':
      return compareClicks(a, b, sortAttribute, asc)
    default:
      return compareString(a, b, sortAttribute, asc)
  }
}

export const sortData = (data, sortAttribute, asc, handleNumeric = false) =>
  data.sort((a, b) => compareResults(a, b, sortAttribute, asc, handleNumeric))

export const randomColor = (i) => {
  const colors = [
    palette.orange,
    palette.magentaBlue,
    palette.green,
    palette.yellow,
    palette.red,
    palette.lightBlue,
    palette.brightOrange,
    palette.pink,
    palette.dullGreen,
    palette.dullYellow,
    palette.dullOrange,
    palette.lightRed,
    palette.limeYellow,
    palette.info,
    palette.darkRed,
    palette.dullRed,
    palette.darkGreen,
    palette.lightGreen,
    palette.blue,
    palette.darkYellow,
    palette.blue70,
    palette.dimBlue,
  ]
  return colors[i % 22]
}

export const getProgressColor = (progress) => {
  if (progress > 74) return palette.red
  if (progress > 49) return palette.yellow
  return palette.green
}

export const getOS = () => {
  const OS_Name = navigator.appVersion
  if (OS_Name.indexOf('Win') !== -1) {
    return 'Win'
  }
  if (OS_Name.indexOf('Mac') !== -1) {
    return 'Mac'
  }
  if (OS_Name.indexOf('X11') !== -1) {
    return 'X11'
  }
  if (OS_Name.indexOf('Linux') !== -1) {
    return 'Linux'
  }
  if (OS_Name.indexOf('SunOS') !== -1) {
    return 'SunOS'
  }
  return 'Win'
}

export const removeHttp = (url, trimWWWW = true) =>
  url
    ? url.replace(
        trimWWWW
          ? /^(?:https?:)?(?:\/\/)?(?:www\.)?/i
          : /^(?:https?:)?(?:\/\/)?/i,
        '',
      )
    : ''

export const addHttp = (url) => (url.match(/^https?:/) ? url : `//${url}`)

export const generateDomainTopCountries = (trafficData, availableCountries) => {
  if (!trafficData || !Array.isArray(trafficData) || !availableCountries) {
    return []
  }

  const countryList = trafficData
    .map((countryTraffic) => {
      if (
        !countryTraffic ||
        !countryTraffic.locationId ||
        !countryTraffic.lang
      ) {
        return null
      }

      const matchingCountry = find(availableCountries, (countryInfo) => {
        return (
          countryInfo &&
          countryTraffic.locationId === `${countryInfo.id}` &&
          countryTraffic.lang === countryInfo.languageCode
        )
      })

      if (!matchingCountry || !matchingCountry.countryIsoCode) {
        return null
      }

      const countryName = translate(
        `${matchingCountry.countryIsoCode.toLowerCase()}_region`,
      )

      return {
        id: `${countryTraffic.lang}_${countryTraffic.locationId}`,
        text: `${countryName} / ${matchingCountry.languageCode.toUpperCase()}`,
        shortText: `${matchingCountry.countryIsoCode.toUpperCase()} / ${matchingCountry.languageCode.toUpperCase()}`,
        locId: countryTraffic.locationId,
        countryCode: matchingCountry.countryIsoCode,
        total: Math.round(countryTraffic.searchTraffic || 0),
        lang: countryTraffic.lang,
      }
    })
    .filter(Boolean) // Remove any null entries

  return orderBy(countryList, ['total'], ['desc'])
}

export const chooseTag = (type, value) => {
  switch (type) {
    case 'organic': {
      if (isNaN(value)) return null
      if (value <= 5000) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 50000)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 100000)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'paid': {
      if (isNaN(value)) return null
      if (value <= 400) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 5000)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 10000)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'traffic': {
      if (isNaN(value)) return null
      if (value <= 25000) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 50000)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 250000)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'domain': {
      if (isNaN(value)) return null
      if (value <= 30) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 45)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 75)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'backlinks': {
      if (isNaN(value)) return null
      if (value <= 10000) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 50000)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 100000)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'domains': {
      if (isNaN(value)) return null
      if (value <= 500) return null
      // return { tag: 'bad', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 10000)
        return {
          tag: 'good',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 50000)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'amazing',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'sd': {
      if (isNaN(value)) return null
      if (value <= 35)
        return {
          tag: 'easy',
          bgColor: palette.success,
          borderColor: palette.darkGreen,
        }
      if (value <= 69)
        return {
          tag: 'medium',
          bgColor: palette.warning,
          borderColor: palette.darkYellow,
        }
      return {
        tag: 'hard',
        bgColor: palette.danger,
        borderColor: palette.darkRed,
      }
    }
    case 'seo_difficulty': {
      if (isNaN(value)) return null
      if (value <= 30)
        return {
          tag: 'easy',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      // if (value <= 60)
      // return { tag: 'medium', bgColor: setOpacity(palette.warning, 0.4), borderColor: palette.warning }
      return null
      // return { tag: 'hard', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
    }
    case 'paid_difficulty': {
      if (isNaN(value)) return null
      if (value <= 30)
        return {
          tag: 'easy',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      // if (value <= 60)
      // return { tag: 'medium', bgColor: setOpacity(palette.warning, 0.4), borderColor: palette.warning }
      return null
      // return { tag: 'hard', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
    }
    case 'search': {
      if (isNaN(value)) return null
      if (value <= 2000) return null
      // return { tag: 'low', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 8000)
        return {
          tag: 'average',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      return {
        tag: 'high',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'score': {
      if (isNaN(value)) return null
      if (value <= 50) return null
      // return { tag: 'poor', bgColor: setOpacity(palette.danger, 0.4), borderColor: palette.red }
      if (value <= 70) return null
      // return { tag: 'fair', bgColor: setOpacity(palette.warning, 0.4), borderColor: palette.warning }
      return {
        tag: 'great',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
    case 'load_time': {
      if (isNaN(value)) return null
      if (value < 2.5)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value < 4)
        return {
          tag: 'needs_improvement',
          bgColor: setOpacity(palette.warning, 0.4),
          borderColor: palette.warning,
        }
      return {
        tag: 'poor',
        bgColor: setOpacity(palette.danger, 0.4),
        borderColor: palette.danger,
      }
    }
    case 'interactivity': {
      if (isNaN(value)) return null
      if (value < 100)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value < 300)
        return {
          tag: 'needs_improvement',
          bgColor: setOpacity(palette.warning, 0.4),
          borderColor: palette.warning,
        }
      return {
        tag: 'poor',
        bgColor: setOpacity(palette.danger, 0.4),
        borderColor: palette.red,
      }
    }
    case 'interactivity_fallback': {
      if (isNaN(value)) return null
      if (value <= 200)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value <= 600)
        return {
          tag: 'needs_improvement',
          bgColor: setOpacity(palette.warning, 0.4),
          borderColor: palette.warning,
        }
      return {
        tag: 'poor',
        bgColor: setOpacity(palette.danger, 0.4),
        borderColor: palette.red,
      }
    }
    case 'visual_stability': {
      if (isNaN(value)) return null
      if (value < 0.1)
        return {
          tag: 'great',
          bgColor: setOpacity(palette.green, 0.4),
          borderColor: palette.green,
        }
      if (value < 0.25)
        return {
          tag: 'needs_improvement',
          bgColor: setOpacity(palette.warning, 0.4),
          borderColor: palette.warning,
        }
      return {
        tag: 'poor',
        bgColor: setOpacity(palette.danger, 0.4),
        borderColor: palette.danger,
      }
    }
    default: {
      if (isNaN(value)) return null
      return {
        tag: 'great',
        bgColor: setOpacity(palette.green, 0.4),
        borderColor: palette.green,
      }
    }
  }
}

export const fillMsGraph = (ms) => {
  const currentYearMonth = moment()
    .startOf('month')
    .subtract(1, 'days')
    .format('YYYYMM')

  // last month is at last
  let msKeys = Object.keys(ms).sort()
  if (msKeys.length === 0) return {}
  const lastValue = ms[msKeys[msKeys.length - 1]]

  while (msKeys[msKeys.length - 1] < currentYearMonth) {
    const nextMonth = moment(msKeys[msKeys.length - 1], 'YYYYMM')
      .endOf('month')
      .add(1, 'days')
      .format('YYYYMM')
    ms[nextMonth] = lastValue
    msKeys.push(nextMonth)
  }
  while (msKeys.length < 12) {
    const previousMonth = moment(msKeys[0], 'YYYYMM')
      .startOf('month')
      .subtract(1, 'days')
      .format('YYYYMM')
    ms[previousMonth] = 0
    msKeys.unshift(previousMonth)
  }

  msKeys = msKeys.slice(-12)
  const output = {}
  msKeys.forEach((key) => {
    output[key] = ms[key]
  })
  return output
}

export const removeWWW = (str) => {
  if (str.includes('://')) {
    return str.replace(/www\./, '')
  }
  return str.replace(/^www\./, '')
}

export const detectSearchType = (input) => {
  if (isBaseUrl(input)) {
    return 'domain'
  }

  if (isValidURL(input) || isDomain(input)) {
    return 'url'
  }

  return 'keyword'
}

export const isNumeric = (input) => {
  if (typeof input !== 'string') return false // we only process strings!
  return !isNaN(input) && !isNaN(parseFloat(input))
}

export const isKeyword = (input) => input && input.trim() && !isDomain(input)

export const getDomain = (s, mode = 'domain') => {
  let url
  let urlString = (s || '').trim().replace(/ /g, '%20')
  if (!urlString) {
    return ''
  }
  const hasRelativeProtocol = urlString.startsWith('//')
  const isRelativeUrl = !hasRelativeProtocol && /^\.*\//.test(urlString)

  // Prepend protocol
  if (!isRelativeUrl) {
    urlString = urlString.replace(/^(?!(?:\w+:)?\/\/)|^\/\//, 'http://')
  }

  if (isDomainWithoutHttp(s)) {
    url = new window.URL(`http://${s}`)
  } else if (isValidURL(urlString)) {
    url = new window.URL(urlString)
  } else {
    return ''
  }
  let hostname
  switch (mode) {
    case 'host':
      hostname = url.hostname
      break
    case 'origin_url':
      hostname = url.href
      break
    case 'url':
      hostname = url.href.replace(/www\./, '')
      break
    case 'prefix':
      hostname = `${removeWWW(url.hostname)}${url.pathname}`
      break
    case 'pathname':
      hostname = `${url.pathname}/`.replace('//', '/')
      break
    case 'domain':
    default:
      hostname = removeWWW(url.hostname)
      break
  }
  return hostname
}

export const detectIE = () => {
  const ua = window.navigator.userAgent
  const msie = ua.indexOf('MSIE ')
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10)
  }

  const trident = ua.indexOf('Trident/')
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:')
    return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10)
  }

  const edge = ua.indexOf('Edge/')
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10)
  }

  // other browser
  return false
}

export const detectSafari = () =>
  !!navigator.userAgent.match(/Version\/[\d]+.*Safari/)

export const escapeRegExp = (string) => {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export const generateRegExp = (words) => {
  const reg = words.map((w) => escapeRegExp(w)).join('|')
  return new RegExp(`(${reg})`, 'gi')
}

export const sanitizeDomain = (domain) => {
  const domainRegex = /([a-z0-9|-]+\.)*[a-z0-9|-]+\.[a-z]+/
  if (domain) {
    try {
      domain = getDomain(domain)
    } catch (e) {
      return null
    }
    const lowercased = domain.toLowerCase().trim()
    const strippedMethod = removeHttp(lowercased)
    const [withoutPath] = strippedMethod.split('/')
    const [withoutParams] = withoutPath.split('?')
    if (domainRegex.test(withoutParams)) {
      return withoutParams
    }
    return null
  }
  return null
}

export const getKeywordsCount = (config = {}) =>
  Object.entries(config).reduce((acc, [, keywordsConfig]) => {
    return acc + keywordsConfig.length
  }, 0)

export const getLocationKeywordsCount = (data, key) => {
  let total = 0
  const counts = {}
  if (!data || data.length === 0) {
    counts.all_all = 0
    return counts
  }

  data.forEach((item) => {
    const generatedKey = generateKey([item.language, item.loc_id], '_')
    counts[generatedKey] = counts[generatedKey] ? counts[generatedKey] + 1 : 1
    total += counts[generatedKey]
  })
  counts.all_all = total
  return counts[key]
}

export const buildDesktopMobileGraphData = (
  keywordInformation = {},
  shouldBlock = false,
) => {
  const { ms, clickstreamData } = keywordInformation
  if (!ms) return { noData: true }

  const keys = Object.keys(ms)
  if (keys.length === 0) return { noData: true }
  const labels = keys.map((key) => key)

  if (!clickstreamData) {
    const desktop = keys.map((key, index) => {
      if (!shouldBlock) {
        return ms[key]
      }
      if (index < keys.length - 4) return 0
      return ms[key]
    })
    const mobile = keys.map(() => 0)

    return {
      labels: keys,
      desktop,
      mobile,
    }
  }
  const { desktopSearchesPercentages, mobileSearchesPercentages } =
    clickstreamData
  const filledPercentages1 = fillMsGraph(desktopSearchesPercentages)
  const filledPercentages2 = fillMsGraph(mobileSearchesPercentages)
  const desktop = keys.map((key, index) => {
    if (!shouldBlock) return ms[key] * filledPercentages1[key]
    if (index < keys.length - 4) return 0
    return ms[key] * filledPercentages1[key]
  })
  const mobile = keys.map((key, index) => {
    if (!shouldBlock) return ms[key] * filledPercentages2[key]
    if (index < keys.length - 4) return 0
    return ms[key] * filledPercentages2[key]
  })

  return { labels, mobile, desktop }
}

export const buildAgeGraphData = (keywordInformation = {}) => {
  const { clickstreamData } = keywordInformation
  const labels = []
  const colors = []
  const data = {}

  if (!clickstreamData) {
    return { data: null, colors, labels, noData: true }
  }

  const {
    ageGroupUnder18Percentage,
    ageGroup18To24Percentage,
    ageGroup25To34Percentage,
    ageGroup35To44Percentage,
    ageGroup45To54Percentage,
    ageGroup55To64Percentage,
    ageGroupPlus65Percentage,
  } = clickstreamData

  const dataRaw = {
    '<18': ageGroupUnder18Percentage,
    '18-24': ageGroup18To24Percentage,
    '25-34': ageGroup25To34Percentage,
    '35-44': ageGroup35To44Percentage,
    '45-54': ageGroup45To54Percentage,
    '55-64': ageGroup55To64Percentage,
    '65+': ageGroupPlus65Percentage,
  }

  const ageRangeColors = {
    '<18': palette.orange20,
    '18-24': palette.orange50,
    '25-34': palette.orange,
    '35-44': palette.blue,
    '45-54': palette.blue70,
    '55-64': palette.blue30,
    '65+': palette.blue10,
  }
  let total = 0
  Object.keys(dataRaw).forEach((key) => {
    total += dataRaw[key]
  })
  Object.keys(dataRaw).forEach((key) => {
    data[ageRangeColors[key]] = [floor((dataRaw[key] * 100) / total, 6)]
    labels.push(key)
    colors.push(ageRangeColors[key])
  })
  return {
    data,
    colors,
    labels,
  }
}

export const buildClickGraphData = (keywordInformation = {}) => {
  const { clickstreamData } = keywordInformation
  const labels = []
  const colors = []
  const data = {}

  const keyToColor = {
    seo: palette.orange70,
    paid: palette.blue70,
    no_clicks: palette.lightGray50,
  }

  if (!clickstreamData) {
    return { data: null, colors, labels, noData: true }
  }
  const { paidSearchesPercentage, organicSearchesPercentage, clickRate } =
    clickstreamData

  const dataRaw = {
    seo: organicSearchesPercentage,
    paid: paidSearchesPercentage,
    no_clicks: 1 - clickRate,
  }

  let total = 0
  Object.keys(dataRaw).forEach((key) => {
    total += dataRaw[key]
  })

  Object.keys(dataRaw).forEach((key) => {
    labels.push(key)
    colors.push(keyToColor[key])
    data[keyToColor[key]] = [floor((dataRaw[key] * 100) / total, 6)]
  })

  return {
    seo: organicSearchesPercentage,
    paid: paidSearchesPercentage,
    no_clicks: 1 - clickRate,
    data,
    colors,
    labels,
  }
}

export const formatNewLostData = (data, shouldBlock = false) => {
  const noData = !(data && data.length > 0)
  if (noData) {
    return { noData: true, labels: [], data: [] }
  }

  const MAX_DAYS_LENGTH = 30

  const timeFormat = 'YYYY-MM-DD'
  const timeUntil = moment()
    .subtract(MAX_DAYS_LENGTH, 'days')
    .format('YYYY-MM-DD')
  const timeUnit = 'days'

  while (data[data.length - 1].date >= timeUntil) {
    const date = moment(data[data.length - 1].date, timeFormat)
      .subtract(1, timeUnit)
      .format(timeFormat)
    data.push({
      date,
      new: 0,
      lost: 0,
    })
  }

  const graphData = data.reduce(
    (result, item, index) => {
      if (shouldBlock && index >= 10) {
        result.data.new.unshift(0)
        result.data.lost.unshift(0)
      } else {
        result.data.new.unshift(item.new)
        result.data.lost.unshift(item.lost > 0 ? -item.lost : item.lost)
      }
      result.labels.unshift(item.date)
      return result
    },
    {
      noData: false,
      labels: [],
      data: {
        new: [],
        lost: [],
      },
    },
  )

  return graphData
}

export const formatOverTimeData = (data, shouldBlock) => {
  const noData = !(data && data.length > 0)
  if (noData) {
    return { noData: true, labels: [], backlinks: [], referringDomains: [] }
  }

  const MAX_MONTHS_LENGTH = 32

  let timeFormat = 'YYYY-MM-DD'
  let timeUntil = moment()
    .subtract(MAX_MONTHS_LENGTH, 'month')
    .format('YYYY-MM-DD')
  let timeUnit = 'days'

  if (process.env.REACT_APP_MOZ_BACKLINKS_HISTORY_FLAG === 'true') {
    timeFormat = 'YYYY-MM'
    timeUntil = moment().subtract(MAX_MONTHS_LENGTH, 'month').format('YYYY-MM')
    timeUnit = 'months'
  }

  while (data[data.length - 1].date >= timeUntil) {
    const date = moment(data[data.length - 1].date, timeFormat)
      .subtract(1, timeUnit)
      .format(timeFormat)
    data.push({
      date,
      backlinks: 0,
      refdomains: 0,
    })
  }

  const graphData = data.reduce(
    (result, item, index) => {
      result.labels.unshift(item.date)
      if (shouldBlock && index >= 4) {
        result.backlinks.unshift(0)
        result.referringDomains.unshift(0)
      } else {
        result.backlinks.unshift(item.backlinks)
        result.referringDomains.unshift(item.refdomains)
      }

      return result
    },
    { noData: false, labels: [], backlinks: [], referringDomains: [] },
  )

  return graphData
}

export const calcExpiryPlanDate = (currentPlan, status, endOfCurrentPeriod) => {
  const trialEnd = moment(endOfCurrentPeriod, 'YYYY-MM-DD')
  if (status === 'trialing') {
    if (currentPlan === 'monthly') {
      trialEnd.add(1, 'months')
    } else if (currentPlan === 'yearly') {
      trialEnd.add(1, 'years')
    }
  }
  return formatDate(trialEnd.format('YYYY-MM-DD'))
}

export const getTimeLeft = (untilTo) => {
  if (isPaidUser() && !untilTo) {
    return 0
  }
  if (!isPaidUser() && !untilTo) {
    return (3 * 60 + 42) * 60 * 1000
  }
  const timeLeft = moment(untilTo).diff(moment())
  if (timeLeft === 0 && !isPaidUser()) {
    return (3 * 60 + 42) * 60 * 1000
  }
  return timeLeft
}

export const isLifetimeValid = () => {
  return true // WE Enable lifetime always for now.
  // return getTimeLeft(untilTo) > 0
}
export const sendMessage = (message, info) =>
  window.postMessage({ message, info }, window.location.origin)

export const calculatePV = (item) => {
  let pv
  const { new_position, old_position, status } = item

  if (status !== 'ok') {
    pv = null
  } else {
    pv = !old_position?.position
      ? 100 - new_position.position
      : old_position.position - new_position.position
  }

  return pv
}
export const sortTrackedKWRows = (
  rows,
  mode,
  modifier,
  filter,
  newSDValues = {},
) => {
  const data = (
    filter
      ? rows.filter((item) => item.new_position?.url?.includes(filter))
      : rows
  ).map((item) => ({
    ...item,
    id: generateKey([item.language, item.loc_id, item.keyword]),
  }))

  const getPosition = (item) => {
    if (item.new_position?.position) return item.new_position?.position
    if (item.new_position?.status === 'pending') return 101
    return 100
  }

  const getSDNewPosition = (key, item, index) => {
    if (mode === 'sd') return newSDValues[key]?.sd || item?.[mode]
    if (mode === 'position') return item[index]?.[mode]
    return item[mode]
  }

  const getValueWithModifier = (value) => {
    if (value === null) return 100 * modifier
    if (value === 'pending') return 101 * modifier
    return value
  }

  const getPositionModePosition = (posA, posB) => {
    if (posA >= 100) {
      return posA === posB ? 0 : 1
    }
    if (posB >= 100) {
      return -1
    }
    return modifier * (posA - posB)
  }

  function compareValues(pvA, pvB) {
    if (pvA === null) {
      return 1
    }
    if (pvB === null) {
      return -1
    }
    return modifier * (pvA - pvB)
  }

  function compareWithModifier(valueA, valueB, altValueA, altValueB) {
    if (valueA === valueB) {
      if (altValueA > altValueB) {
        return -1 * modifier
      }
      if (altValueA < altValueB) {
        return 1 * modifier
      }
      return 0
    }
    return valueA > valueB ? -1 * modifier : 1 * modifier
  }

  const sortedData = data.sort((itemA, itemB) => {
    const aIndex = 'new_position'
    const bIndex = 'new_position'
    const altMode = mode === 'position' ? 'volume' : 'position'

    const posA = getPosition(itemA)
    const posB = getPosition(itemB)

    const [keyA, keyB] = [itemA.id, itemB.id]

    const [pvA, pvB] = [itemA, itemB].map(calculatePV)
    const [valueA, valueB] = [
      getSDNewPosition(keyA, itemA, aIndex),
      getSDNewPosition(keyB, itemB, bIndex),
    ].map((value) => getValueWithModifier(value))
    const [altValueA, altValueB] = [
      altMode === 'position' ? itemA[aIndex]?.[altMode] : itemA[altMode],
      altMode === 'position' ? itemB[bIndex]?.[altMode] : itemB[altMode],
    ].map((altValue) => (altValue === null ? 100 * modifier : altValue))

    if (mode === 'position') {
      return getPositionModePosition(posA, posB)
    }
    if (mode === 'pv') {
      return compareValues(pvA, pvB)
    }
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return modifier > 0
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA)
    }
    return compareWithModifier(valueA, valueB, altValueA, altValueB)
  })

  return sortedData
}

export function openInNewTab(url, replace = false) {
  const win = window.open(url, replace ? '_self' : '_blank')
  win.focus()
}

export const getMetricOption = (title) => {
  if (title === translate('search_volume'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.SEARCH_VOLUME

  if (title === translate('seo_difficulty'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.SEO_DIFFICULT

  if (title === translate('paid_difficulty'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.PAID_DIFFICULT

  if (title === translate('cpc_full'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.COST_PER_CLICK

  if (title === translate('organic_keywords'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.ORGANIC_KEYWORDS

  if (title === translate('domain_authority'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.DOMAIN_AUTHORITY

  if (title === translate('referring_domains'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.REFERRING_DOMAINS

  if (title === translate('backlinks'))
    return MIXPANEL_PROPS.METRIC_SELECTED.values.BACKLINKS

  return MIXPANEL_PROPS.METRIC_SELECTED.values.PAID_KEYWORDS
}

export const formatChartDate = (
  date,
  readFormat = 'YYYYMM',
  outputFormat = '',
  abbreviatedMonth = false,
) => {
  let dateObj
  if (readFormat === 'timestamp') {
    dateObj = moment(date)
  } else {
    dateObj = moment(date, readFormat)
  }

  if (outputFormat === 'MMM YYYY') {
    return `${translate(
      dateObj.format('MMM').toLowerCase(),
    ).toUpperCase()} ${dateObj.format('YYYY')}`
  }
  if (outputFormat === 'MMMM YYYY') {
    return `${translate(
      dateObj.format('MMMM').toLowerCase(),
    ).toUpperCase()} ${dateObj.format('YYYY')}`
  }
  if (outputFormat === 'MMM DD') {
    return `${translate(
      dateObj.format('MMM').toLowerCase(),
    ).toUpperCase()} ${dateObj.format('DD')}`
  }
  if (outputFormat === 'MMM DD, YYYY') {
    return `${translate(
      dateObj.format('MMM').toLowerCase(),
    ).toUpperCase()} ${dateObj.format('DD')}, ${dateObj.format('YYYY')}`
  }
  if (outputFormat === 'MMM DD YYYY') {
    return `${translate(
      dateObj.format('MMM').toLowerCase(),
    ).toUpperCase()} ${dateObj.format('DD')} ${dateObj.format('YYYY')}`
  }
  if (outputFormat === 'MMMM DD, YYYY') {
    return `${translate(dateObj.format('MMMM').toLowerCase())} ${dateObj.format(
      'DD',
    )}, ${dateObj.format('YYYY')}`
  }
  if (outputFormat === 'MMMM') {
    return `${translate(dateObj.format('MMMM').toLowerCase())}`
  }
  if (outputFormat === 'LLL') {
    return `${translate(
      dateObj.format(abbreviatedMonth ? 'MMM' : 'MMMM').toLowerCase(),
    )} ${dateObj.format('DD')}, ${dateObj.format('YYYY')} ${dateObj.format(
      'HH:mm A',
    )}`
  }
}

export function removeLineBreaks(value) {
  return value.replace(/(\r\n|\n|\r)/gm, '')
}

// TAKEN FROM - https://github.com/janl/mustache.js/blob/master/mustache.js#L55
export const htmlEntityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
}

export function escapeHtml(value) {
  return String(value).replace(/[&<>"'`=/]/g, (s) => {
    // @ts-ignore
    return htmlEntityMap[s]
  })
}

export function safeHtmlString(value) {
  return escapeHtml(removeLineBreaks(value))
}

export function splitByLineBreakOrComma(value) {
  return value
    .split(/[\n,]/g)
    .filter((v) => !!v.trim())
    .map((v) => v.toLowerCase().trim())
}

export function getHash(input) {
  let hash = 0
  const len = input.length
  for (let i = 0; i < len; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0 // to 32bit integer
  }
  return Math.abs(hash)
}

export function weekdaysBetween(d1, d2, isoWeekday) {
  // ensure we have valid moment instances
  d1 = moment(d1)
  d2 = moment(d2)
  // figure out how many days to advance to get to the next
  // specified weekday (might be 0 if d1 is already the
  // specified weekday).
  const daysToAdd = (7 + isoWeekday - d1.isoWeekday()) % 7
  const nextTuesday = d1.clone().add(daysToAdd, 'days')
  // if we are already passed the end date, there must not
  // be any of that day in the given period.
  if (nextTuesday.isAfter(d2)) {
    return 0
  }
  // otherwise, just return the whole number of weeks
  // difference plus one for the day we already advanced to
  const weeksBetween = d2.diff(nextTuesday, 'weeks')
  return weeksBetween + 1
}

export const getEstimatedOrganicTraffic = (organic, { rangeType }) => {
  if (rangeType === 'last30days') return 'N/A'
  const monthCounts = {
    lastmonth: 1,
    last3months: 3,
    last6months: 6,
    last1year: 12,
    last3years: 36,
  }

  return formatNumber(
    Object.values(organic)
      .slice(-monthCounts[rangeType])
      .reduce((total, value) => total + value.searchTraffic, 0),
  )
}

export function isPromise(promise) {
  return !!promise && typeof promise.then === 'function'
}

export const decodeURLString = (url) => {
  try {
    return decodeURI(url)
  } catch (error) {
    return url
  }
}

export function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch (e) {
    return null
  }
}

/*
 ** Generate Key with passed parameters by adding delimiter
 ** Default delimiter is ::
 ** Example: generateKey(['marketing', 'en', 2840]) => 'marketing::en::2840'
 ** Example: generateKey(['marketing', 'en', 2840], '_') => 'marketing_en_2840'
 */
export const generateKey = (args = [], delimiter = '::') => args.join(delimiter)

export const generateOldSdKeywords = (
  data,
  lang,
  loc_id,
  newSDValues = {},
  remainingCredits = 0,
  pageSize = 0,
) => {
  let keywords = data.reduce((acc, item) => {
    const key = generateKey([lang, loc_id, item.keyword])
    if (!isValidSD(newSDValues[key] ?? item)) {
      acc.push({
        keyword: item.keyword,
        lang,
        loc_id,
        updated_at: item.updated_at,
      })
    }
    return acc
  }, [])

  if (remainingCredits) {
    keywords = (
      remainingCredits > pageSize
        ? keywords
            .slice(0, pageSize)
            .sort((a, b) => a.updated_at - b.updated_at)
            .concat(keywords.slice(pageSize))
        : keywords.sort((a, b) => a.updated_at - b.updated_at)
    ).slice(0, remainingCredits)
  }

  return keywords.map((item) => omit(item, 'updated_at'))
}

export const getOldSDKeywords = (
  data,
  newSDValues = {},
  isRankTracking = false,
  remainingCredits = 0,
  pageSize = 0,
) => {
  let keywords = data.reduce((acc, item) => {
    const keyword = get(item, isRankTracking ? 'keyword' : 'keyword')
    const lang = get(item, isRankTracking ? 'language' : 'language')
    const loc_id = get(item, isRankTracking ? 'loc_id' : 'loc_id')
    const updated_at = get(item, isRankTracking ? 'updated_at' : 'updated_at')

    const key = generateKey([lang, loc_id, keyword])

    if (loc_id && !isGlobal(loc_id) && !isValidSD(newSDValues[key] ?? item)) {
      acc.push({ keyword, lang, loc_id, updated_at })
    }
    return acc
  }, [])

  if (remainingCredits) {
    keywords = (
      remainingCredits > pageSize
        ? keywords
            .slice(0, pageSize)
            .sort((a, b) => a.updated_at - b.updated_at)
            .concat(keywords.slice(pageSize))
        : keywords.sort((a, b) => a.updated_at - b.updated_at)
    ).slice(0, remainingCredits)
  }

  return keywords.map((item) => omit(item, 'updated_at'))
}

export const trimAndLowercase = (str = '', lowercase = false) =>
  lowercase ? str.trim().toLowerCase() : str.trim()
/*
 ** Trim, convert to lowercase and limit the keyword length to MAX_KEYWORD_CHARACTER_LENGTH
 ** By default MAX_KEYWORD_CHARACTER_LENGTH = 80 and we're adding ellipsis at the end
 ** Example: truncateKeyword(' Marketing Tool ', false) => 'marketing tool'
 */
export const truncateKeyword = (
  keyword,
  addEllipsis = false,
  lowercase = true,
  maxLength = MAX_KEYWORD_CHARACTER_LENGTH,
) => {
  const trimmedKeyword = trimAndLowercase(keyword, lowercase)
  if (trimmedKeyword)
    return (
      trimmedKeyword.slice(0, maxLength) +
      (trimmedKeyword.length > maxLength && addEllipsis ? '...' : '')
    )
  return trimmedKeyword
}

export const capitalizeText = (text) => {
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Extracts `id` and `languageCode` properties from an array of location objects.
 * @param {Object[]} locations - An array of location objects with `id` and `languageCode` properties.
 * @returns {Object[]} An array of objects with `loc_id` and `lang` properties.
 */
export const mapLocLang = (locations) =>
  locations.map((loc) => ({
    loc_id: loc.id,
    lang: loc.languageCode,
  }))

// timestamp param is in seconds
export const getTimeLabel = (timestamp) => {
  const MS_IN_SEC = 1000
  const MS_IN_MIN = 60 * MS_IN_SEC
  const MS_IN_HOUR = 60 * MS_IN_MIN
  const MS_IN_DAY = 24 * MS_IN_HOUR
  const MS_IN_WEEK = 7 * MS_IN_DAY
  const MS_IN_MONTH = 30 * MS_IN_DAY

  const now = new Date().getTime()
  if (!timestamp) {
    return '-'
  }
  const diff = now - timestamp * MS_IN_SEC

  if (diff < 2 * MS_IN_HOUR) {
    return translate('just_now')
  }
  if (diff < MS_IN_DAY) {
    return translate('today')
  }
  if (diff < 2 * MS_IN_DAY) {
    return translate('yesterday')
  }
  if (diff < MS_IN_WEEK) {
    return translate('this_week')
  }
  if (diff < 2 * MS_IN_WEEK) {
    return translate('one_week_ago')
  }
  if (diff < 3 * MS_IN_WEEK) {
    return translate('n_weeks_ago', 2)
  }
  if (diff < 4 * MS_IN_WEEK) {
    return translate('n_weeks_ago', 3)
  }
  if (diff < 2 * MS_IN_MONTH) {
    return translate('one_month_ago')
  }
  if (diff < 7 * MS_IN_MONTH) {
    return translate('n_months_ago', Math.floor(diff / MS_IN_MONTH))
  }
  return translate('over_6_months')
}

export const isUpdatedNow = (timestamp = new Date(0).getTime() / 1000) => {
  const MS_IN_SEC = 1000
  const MS_IN_MIN = 60 * MS_IN_SEC
  const MS_IN_HOUR = 60 * MS_IN_MIN

  const now = new Date().getTime()
  const diff = now - timestamp * MS_IN_SEC

  return diff < 2 * MS_IN_HOUR
}

export const mergeSDKeywords = (
  keywords,
  newSDValues = {},
  lang = undefined,
  locId = undefined,
) => {
  return keywords.map((item) => {
    const newSDValue =
      newSDValues[
        generateKey([item.language || lang, item.loc_id || locId, item.keyword])
      ]
    return {
      ...item,
      ...newSDValue,
    }
  })
}

export const getAIW2Url = (kw, locId, lang) => {
  return `${AIWRITER_BASE_URL}?kw=${kw}&locId=${locId}&lang=${lang}&from=ubs`
}

export const allowPromptModal = (promptModal) =>
  !skipPromptPaths.some((path) => window.location.pathname.includes(path)) ||
  promptModal.eventProperty?.addProjectButtonLocation ===
    MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values.SIDEBAR_PROJECT_SELECTOR

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent)

export const getExpirationDate = (lifeTime) => {
  const expirationDate = Date.now() + lifeTime * 24 * 60 * 60 * 1000
  return Math.floor(expirationDate / 1000)
}
